import Service from '@ember/service';

import { tracked } from '@glimmer/tracking';

// Tracks which click-to-edit-tinymce instance is currently in edit mode so
// only one editor is open at a time across the page. The previous active
// component is asked to deactivate (which commits any pending content before
// tearing down its TinyMCE editor).
//
// While any editor is active, a document-level mousedown listener watches
// for clicks that land outside TinyMCE's own UI (editor, toolbars, popups —
// all carry the `.tox` class in TinyMCE 5+) and deactivates the active slot
// so it flips back to its preview. Clicks inside the editor iframe don't
// reach the parent document, so typing is unaffected.
export default class ClickToEditCoordinatorService extends Service {
  @tracked
  active = null;

  activate(component) {
    let prev = this.active;
    this.active = component;
    if (prev && prev !== component && !prev.isDestroyed && !prev.isDestroying) {
      prev.deactivate();
    }
    this._ensureOutsideClickListener();
  }

  // Synchronously forward the current editor content to the parent model
  // without tearing down the editor. Used during beforeunload to ensure
  // any content within ember-tinymce's own 10ms debounce window is captured.
  flushActive() {
    let active = this.active;
    if (!active || active.isDestroyed || active.isDestroying) return;
    let editor = active._editorInstance;
    if (editor && !editor.removed) {
      active._emit(editor.getContent());
    }
  }

  deregister(component) {
    if (this.active === component) {
      this._clearActive();
    }
  }

  _clearActive() {
    this.active = null;
    this._removeOutsideClickListener();
  }

  _ensureOutsideClickListener() {
    if (this._outsideClickHandler) return;
    this._outsideClickHandler = (e) => this._onDocumentMouseDown(e);
    document.addEventListener('mousedown', this._outsideClickHandler, true);
  }

  _removeOutsideClickListener() {
    if (!this._outsideClickHandler) return;
    document.removeEventListener('mousedown', this._outsideClickHandler, true);
    this._outsideClickHandler = null;
  }

  _onDocumentMouseDown(e) {
    let active = this.active;
    if (!active || active.isDestroyed || active.isDestroying) {
      this._clearActive();
      return;
    }
    let target = e.target;
    if (target && target.closest && target.closest('.tox')) return;
    active.deactivate();
    this._clearActive();
  }
}
